import { pickLocalizedString, SUPPORTED_LOCALES } from "@/lib/locale";
import { routing } from "@/i18n/routing";
import type { LocaleCode, LocalizedString } from "@/types/sections";

export const SITE_URL = "https://uncommon12.ch";
export const SITE_NAME = "Uncommon 12";
export const SEO_REVALIDATE_SECONDS = 60 * 60;

const SLUG_ID_SEPARATOR = "--";

const localizedPathnames = routing.pathnames as Record<
  string,
  string | Partial<Record<LocaleCode, string>>
>;
const INTERNAL_URL_ORIGIN = "https://uncommon12.local";
const ABSOLUTE_OR_PROTOCOL_HREF = /^[a-z][a-z\d+.-]*:/i;

export type AppPathname = keyof typeof routing.pathnames & string;

export function getLocalizedPathname(
  locale: LocaleCode,
  pathname: AppPathname,
  params: Record<string, string> = {}
) {
  const template = localizedPathnames[pathname];

  if (!template) {
    throw new Error(`Unsupported pathname: ${pathname}`);
  }

  let localized =
    typeof template === "string"
      ? template
      : template[locale] ?? template.en ?? template.fr ?? pathname;

  for (const [key, value] of Object.entries(params)) {
    localized = localized.replace(`[${key}]`, encodeURIComponent(value));
  }

  return `/${locale}${localized === "/" ? "" : localized}`;
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function withoutLocalePrefix(pathname: string) {
  const parts = pathname.split("/").filter(Boolean);

  if (parts.length === 0) {
    return "/";
  }

  if (SUPPORTED_LOCALES.includes(parts[0] as LocaleCode)) {
    return `/${parts.slice(1).join("/")}`;
  }

  return pathname;
}

function getPathnameTemplates(template: string | Partial<Record<LocaleCode, string>>) {
  if (typeof template === "string") {
    return [template];
  }

  return SUPPORTED_LOCALES.map((locale) => template[locale]).filter(Boolean) as string[];
}

function matchPathnameTemplate(pathname: string, template: string) {
  const pathSegments = pathname.split("/").filter(Boolean);
  const templateSegments = template.split("/").filter(Boolean);

  if (pathSegments.length !== templateSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};

  for (let index = 0; index < templateSegments.length; index += 1) {
    const templateSegment = templateSegments[index];
    const pathSegment = pathSegments[index];
    const paramMatch = templateSegment.match(/^\[([^\]]+)\]$/);

    if (paramMatch) {
      params[paramMatch[1]] = safeDecodeURIComponent(pathSegment);
      continue;
    }

    if (templateSegment !== pathSegment) {
      return null;
    }
  }

  return params;
}

function resolveAppPathname(pathname: string) {
  const normalizedPathname = withoutLocalePrefix(pathname) || "/";

  for (const [appPathname, localizedTemplate] of Object.entries(localizedPathnames)) {
    const templates = [appPathname, ...getPathnameTemplates(localizedTemplate)];

    for (const template of templates) {
      const params = matchPathnameTemplate(normalizedPathname, template);

      if (params) {
        return {
          pathname: appPathname as AppPathname,
          params,
        };
      }
    }
  }

  return null;
}

export function getLocalizedHref(locale: LocaleCode, href: string) {
  const trimmedHref = href.trim();

  if (!trimmedHref || trimmedHref === "#") {
    return trimmedHref || "#";
  }

  if (ABSOLUTE_OR_PROTOCOL_HREF.test(trimmedHref) || trimmedHref.startsWith("//")) {
    return trimmedHref;
  }

  if (trimmedHref.startsWith("#")) {
    return `${getLocalizedPathname(locale, "/")}${trimmedHref}`;
  }

  let url: URL;

  try {
    url = new URL(trimmedHref, INTERNAL_URL_ORIGIN);
  } catch {
    return trimmedHref;
  }

  const resolved = resolveAppPathname(url.pathname);
  const suffix = `${url.search}${url.hash}`;

  if (resolved) {
    return `${getLocalizedPathname(locale, resolved.pathname, resolved.params)}${suffix}`;
  }

  const normalizedPathname = withoutLocalePrefix(url.pathname);
  const prefixedPathname =
    normalizedPathname === "/" ? getLocalizedPathname(locale, "/") : `/${locale}${normalizedPathname}`;

  return `${prefixedPathname}${suffix}`;
}

export function getLanguageAlternates(resolvePath: (locale: LocaleCode) => string) {
  return Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [locale, resolvePath(locale)])
  );
}

export function toAbsoluteUrl(path: string) {
  return new URL(path, SITE_URL).toString();
}

export function slugifySegment(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/['’]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function buildContentSegment(label: string, id: string) {
  const slug = slugifySegment(label);
  return slug ? `${slug}${SLUG_ID_SEPARATOR}${id}` : id;
}

export function parseContentSegment(segment: string) {
  const cleaned = decodeURIComponent(segment).replace(/^\/+|\/+$/g, "");

  if (!cleaned) {
    return { id: "", slug: "" };
  }

  const index = cleaned.lastIndexOf(SLUG_ID_SEPARATOR);

  if (index === -1) {
    return { id: cleaned, slug: "" };
  }

  return {
    slug: cleaned.slice(0, index),
    id: cleaned.slice(index + SLUG_ID_SEPARATOR.length),
  };
}

export function buildNewsDetailPath(
  locale: LocaleCode,
  id: string,
  title: LocalizedString | string | undefined
) {
  return getLocalizedPathname(locale, "/news/[id]", {
    id: buildContentSegment(pickLocalizedString(title, locale, id), id),
  });
}

export function buildGalleryDetailPath(locale: LocaleCode, id: string, title: string) {
  return getLocalizedPathname(locale, "/gallery/[id]", {
    id: buildContentSegment(title || id, id),
  });
}
